import { useCallback } from 'react'

import BellIcon from '@heroicons/react/outline/esm/BellIcon.js'
import { clsx } from 'clsx'

import { Post } from '@tribeplatform/gql-client/types'
import { useUpdateMemberPostNotificationSettings } from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'

import { useLogin } from '../../common/hooks/useLogin.js'
import { useI18n } from '../../i18n/providers/I18nProvider.js'

interface Props {
  post: Post
  showIcon?: boolean
  showText?: boolean
}
export const FollowAction = ({
  post,
  showIcon = true,
  showText = true,
}: Props) => {
  const intl = useI18n()
  const { isLoggedIn, showLogin } = useLogin()

  const { isLoading, mutate: updateMemberPostNotificationSettings } =
    useUpdateMemberPostNotificationSettings()

  const isFollowed =
    post?.authMemberProps?.memberPostNotificationSettingsEnabled

  const handleClick = useCallback(
    e => {
      if (!isLoggedIn) {
        e.preventDefault()
        return showLogin()
      }
      if (isLoading) {
        return
      }

      updateMemberPostNotificationSettings({
        input: {
          enabled: !isFollowed,
        },
        postId: post?.id,
      })
    },
    [
      isFollowed,
      isLoading,
      post?.id,
      updateMemberPostNotificationSettings,
      isLoggedIn,
      showLogin,
    ],
  )

  const leadingIcon = isFollowed ? (
    <BellIcon className="w-6 h-6 -rotate-12 shrink-0" />
  ) : (
    <BellIcon className="w-6 h-6 transition-transform group-hover:-rotate-12 shrink-0" />
  )

  return (
    <div className="flex-1 flex min-w-0 h-full">
      <Button
        variant="outline"
        className={clsx(
          'group select-none',
          isFollowed && 'bg-surface-subdued hover:bg-surface-hovered',
        )}
        size="md"
        fullWidth
        leadingIcon={showIcon ? leadingIcon : null}
        onClick={handleClick}
      >
        {showText && (
          <span className="truncate">
            {isFollowed
              ? intl.$t({
                  id: 'Generics.Following',
                  defaultMessage: 'Following',
                })
              : intl.$t({
                  id: 'Generics.Follow',
                  defaultMessage: 'Follow',
                })}
          </span>
        )}
      </Button>
    </div>
  )
}
